import React from 'react';
import { useNavigate } from 'react-router-dom';

function CookiePolicy() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-4xl mx-auto px-6 py-12">
        {/* Back Button */}
        <button
          onClick={() => navigate('/')}
          className="mb-8 flex items-center gap-2 text-purple-600 hover:text-purple-700 font-medium"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
          </svg>
          Back to Home
        </button>

        {/* Header */}
        <div className="mb-12">
          <h1 className="text-4xl font-bold text-gray-900 mb-4">Cookie Policy</h1>
          <p className="text-gray-600">Last Updated: January 25, 2025</p>
        </div>

        {/* Content */}
        <div className="prose prose-lg max-w-none">
          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">1. Introduction</h2>
            <p className="text-gray-700 mb-4">
              ZuperBooks, LLC ("we," "our," or "us") uses cookies and similar tracking technologies on our website and platform. This Cookie Policy explains what cookies are, how we use them, and your choices regarding cookies.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">2. What Are Cookies?</h2>
            <p className="text-gray-700 mb-4">
              Cookies are small text files that are placed on your device when you visit a website. They help the website remember your preferences and improve your browsing experience.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">3. Types of Cookies We Use</h2>
            
            <h3 className="text-xl font-semibold text-gray-900 mb-3">3.1 Essential Cookies</h3>
            <p className="text-gray-700 mb-4">
              These cookies are necessary for the website to function properly and cannot be disabled.
            </p>
            <ul className="list-disc pl-6 text-gray-700 mb-4 space-y-2">
              <li><strong>Session Cookies:</strong> Maintain your login session</li>
              <li><strong>Security Cookies:</strong> Detect authentication abuse and protect user accounts</li>
              <li><strong>Load Balancing:</strong> Distribute traffic across servers</li>
            </ul>

            <h3 className="text-xl font-semibold text-gray-900 mb-3">3.2 Functional Cookies</h3>
            <p className="text-gray-700 mb-4">
              These cookies enable enhanced functionality and personalization.
            </p>
            <ul className="list-disc pl-6 text-gray-700 mb-4 space-y-2">
              <li><strong>Preference Cookies:</strong> Remember your settings (e.g., dark/light mode, language)</li>
              <li><strong>User Interface:</strong> Remember your navigation preferences</li>
              <li><strong>Feature Flags:</strong> Enable specific features for your account</li>
            </ul>

            <h3 className="text-xl font-semibold text-gray-900 mb-3">3.3 Analytics Cookies</h3>
            <p className="text-gray-700 mb-4">
              These cookies help us understand how visitors interact with our platform.
            </p>
            <ul className="list-disc pl-6 text-gray-700 mb-4 space-y-2">
              <li><strong>Usage Analytics:</strong> Track page views, session duration, and feature usage</li>
              <li><strong>Performance Monitoring:</strong> Identify technical issues and optimize performance</li>
              <li><strong>A/B Testing:</strong> Test new features and improvements</li>
            </ul>

            <h3 className="text-xl font-semibold text-gray-900 mb-3">3.4 Marketing Cookies</h3>
            <p className="text-gray-700 mb-4">
              These cookies track your visits across websites to display relevant ads.
            </p>
            <ul className="list-disc pl-6 text-gray-700 mb-4 space-y-2">
              <li><strong>Advertising Cookies:</strong> Deliver targeted advertisements</li>
              <li><strong>Social Media:</strong> Enable social sharing and track referrals</li>
              <li><strong>Retargeting:</strong> Show ads to users who visited our site</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">4. Specific Cookies Used</h2>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200 mb-4">
                <thead className="bg-gray-100">
                  <tr>
                    <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Cookie Name</th>
                    <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Purpose</th>
                    <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Duration</th>
                    <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Type</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  <tr>
                    <td className="px-4 py-3 text-sm text-gray-700">session_id</td>
                    <td className="px-4 py-3 text-sm text-gray-700">Maintain user login session</td>
                    <td className="px-4 py-3 text-sm text-gray-700">Session</td>
                    <td className="px-4 py-3 text-sm text-gray-700">Essential</td>
                  </tr>
                  <tr>
                    <td className="px-4 py-3 text-sm text-gray-700">theme_preference</td>
                    <td className="px-4 py-3 text-sm text-gray-700">Remember dark/light mode</td>
                    <td className="px-4 py-3 text-sm text-gray-700">1 year</td>
                    <td className="px-4 py-3 text-sm text-gray-700">Functional</td>
                  </tr>
                  <tr>
                    <td className="px-4 py-3 text-sm text-gray-700">_ga</td>
                    <td className="px-4 py-3 text-sm text-gray-700">Google Analytics tracking</td>
                    <td className="px-4 py-3 text-sm text-gray-700">2 years</td>
                    <td className="px-4 py-3 text-sm text-gray-700">Analytics</td>
                  </tr>
                  <tr>
                    <td className="px-4 py-3 text-sm text-gray-700">_gid</td>
                    <td className="px-4 py-3 text-sm text-gray-700">Google Analytics session</td>
                    <td className="px-4 py-3 text-sm text-gray-700">24 hours</td>
                    <td className="px-4 py-3 text-sm text-gray-700">Analytics</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">5. Third-Party Cookies</h2>
            <p className="text-gray-700 mb-4">
              We use third-party services that may set their own cookies:
            </p>
            <ul className="list-disc pl-6 text-gray-700 mb-4 space-y-2">
              <li><strong>Google OAuth:</strong> For authentication services</li>
              <li><strong>Google Analytics:</strong> For usage analytics</li>
              <li><strong>Stripe:</strong> For payment processing (when applicable)</li>
              <li><strong>Cloud Infrastructure:</strong> AWS, MongoDB Atlas for hosting</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">6. Your Cookie Choices</h2>
            
            <h3 className="text-xl font-semibold text-gray-900 mb-3">6.1 Browser Settings</h3>
            <p className="text-gray-700 mb-4">
              Most browsers allow you to control cookies through their settings. You can:
            </p>
            <ul className="list-disc pl-6 text-gray-700 mb-4 space-y-2">
              <li>Block all cookies</li>
              <li>Accept only first-party cookies</li>
              <li>Delete cookies after each session</li>
              <li>View and manage existing cookies</li>
            </ul>
            <p className="text-gray-700 mb-4">
              <strong>Note:</strong> Blocking essential cookies may prevent the platform from functioning properly.
            </p>

            <h3 className="text-xl font-semibold text-gray-900 mb-3">6.2 Opt-Out Links</h3>
            <ul className="list-disc pl-6 text-gray-700 mb-4 space-y-2">
              <li><strong>Google Analytics:</strong> <a href="https://tools.google.com/dlpage/gaoptout" className="text-purple-600 hover:underline" target="_blank" rel="noopener noreferrer">Google Analytics Opt-out</a></li>
              <li><strong>Do Not Track:</strong> We honor browser Do Not Track (DNT) signals</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">7. Mobile Devices</h2>
            <p className="text-gray-700 mb-4">
              On mobile devices, you can control tracking through:
            </p>
            <ul className="list-disc pl-6 text-gray-700 mb-4 space-y-2">
              <li><strong>iOS:</strong> Settings → Privacy → Tracking</li>
              <li><strong>Android:</strong> Settings → Google → Ads → Opt out of Ads Personalization</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">8. Changes to This Policy</h2>
            <p className="text-gray-700 mb-4">
              We may update this Cookie Policy to reflect changes in our practices or for legal reasons. We will notify you of significant changes by posting a notice on our website.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">9. Contact Us</h2>
            <p className="text-gray-700 mb-4">
              If you have questions about our use of cookies, please contact us:
            </p>
            <div className="bg-gray-100 p-6 rounded-lg">
              <p className="text-gray-700"><strong>ZuperBooks, LLC</strong></p>
              <p className="text-gray-700">Headquarters: Michigan, United States</p>
              <p className="text-gray-700">Email: privacy@zuperbooks.com</p>
              <p className="text-gray-700">Phone: 1-800-ZUPER-BK</p>
            </div>
          </section>
        </div>
      </div>
    </div>
  );
}

export default CookiePolicy;
